import { generateKebabCase, useDateFormatter } from '@nexelem/utils';
import { TaskStatus, TaskTree, TaskTreeItem, ToDoTreeType } from '@nexelem/graphne-api';
import { forwardRef, SetStateAction, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AvailableTaskTypes, DISPLAY_FIELD, ToDoListViewItemProps } from '../../types';
import { getCheckableStatus } from '../../utils';
import { TreeListNodeItem } from '../TreeListNodeItem';
import { useTreeTasksListStore } from '../../store/useTreeTasksListStore';
import { useExpandedStore } from './useExpandedStore';

type TreeListItemProps = ToDoListViewItemProps<TaskTree | TaskTreeItem> & {
  readonly depth: number;
  readonly index: number;
  readonly path?: string;
  readonly isParentExpanded?: boolean;
  readonly displayField: DISPLAY_FIELD;
};

export const TreeListItem = forwardRef<HTMLDivElement, TreeListItemProps>(
  (
    {
      task,
      isCheckAllFilterChecked,
      onMoreClick,
      depth,
      index,
      path,
      onCheck,
      isParentExpanded = false,
      displayField,
    }: TreeListItemProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const defaultValue = useMemo(() => {
      if (task.dependencies?.length) {
        return task.dependencies.some((dependency) => dependency.matchedPath);
      }
      return false;
    }, [task.dependencies]);
    const { setExpanded, getExpanded } = useExpandedStore();
    const isExpanded = getExpanded(task.id, defaultValue);
    const setIsExpanded = useCallback(
      (expanded: SetStateAction<boolean>) => setExpanded(task.id, expanded),
      [setExpanded, task.id],
    );

    const mapIdKey = task.id.toString();
    const dateFormatter = useDateFormatter({ displayWithHours: true });
    const hashMapIdPath = generateKebabCase(path, task.id);

    const treeTasksListCheckMap = useTreeTasksListStore((state) => state.treeTasksListCheckMap);
    const setNewItem = useTreeTasksListStore((state) => state.setNewTreeItem);
    const addNewParent = useTreeTasksListStore((state) => state.addNewParent);

    useEffect(() => {
      if (!treeTasksListCheckMap.has(mapIdKey)) {
        const directParentId = depth !== 0 && path ? path.split('-').slice(-1) : [];
        const childrenIds = task.dependencies?.map((dependency) => dependency.id.toString());
        setNewItem({
          taskId: mapIdKey,
          isCheckable: getCheckableStatus(task.status, task.notPlannedDependencies),
          isOrder: (task as TaskTree).treeType === ToDoTreeType.DispatchOrder,
          taskStatus: task.status,
          directParentId,
          childrenIds,
          isCheckAllFilterChecked,
        });
      } else {
        const selectedTask = treeTasksListCheckMap.get(mapIdKey);
        if (selectedTask && depth > 0 && path) {
          const directParentId = path.split('-').pop();
          if (directParentId && !selectedTask?.parentsIds?.includes(directParentId)) {
            addNewParent(mapIdKey, directParentId);
          }
        }
      }
    }, [addNewParent, treeTasksListCheckMap, depth, mapIdKey, path, setNewItem, task, isCheckAllFilterChecked]);

    const handleTreeTaskCheck = (taskId: string) => (evt: SyntheticEvent) => {
      evt.stopPropagation();
      evt.preventDefault();
      onCheck(taskId);
    };

    const isCheckable = useMemo(
      () => !!task.notPlannedDependencies?.length || task.status === TaskStatus.NotPlanned,
      [task.notPlannedDependencies, task.status],
    );

    return (
      <TreeListNodeItem
        depth={depth}
        displayField={displayField}
        hasDependencies={!!task.dependencies?.length}
        index={index}
        isCheckable={isCheckable}
        isChecked={treeTasksListCheckMap.get(mapIdKey)?.checked || false}
        isExpanded={isExpanded}
        isIntermediate={treeTasksListCheckMap.get(mapIdKey)?.isIntermediate || false}
        isParentExpanded={isParentExpanded}
        key={generateKebabCase(path, task.id, task.dependencies?.length, index)}
        notPlannedDependencies={task.notPlannedDependencies}
        ref={ref}
        setChecked={handleTreeTaskCheck(task.id.toString())}
        task={task}
        getSecondary={(task: AvailableTaskTypes) =>
          task.dueDate && (
            <span className="overflow-hidden text-s11 text-ellipsis whitespace-nowrap">
              {`${t('products.productionPlan.todoList.dueDate')} ${dateFormatter(task.dueDate)}`}
            </span>
          )
        }
        onExpand={setIsExpanded}
        onMoreClick={(e) => onMoreClick(e, task)}
      >
        {task.dependencies && (
          <>
            {task.dependencies.map((dep, index) => (
              <TreeListItem
                depth={depth + 1}
                displayField={displayField}
                index={index}
                isCheckAllFilterChecked={isCheckAllFilterChecked}
                isParentExpanded={isExpanded}
                key={generateKebabCase(hashMapIdPath, dep.id)}
                path={hashMapIdPath}
                task={dep}
                onCheck={onCheck}
                onMoreClick={onMoreClick}
              />
            ))}
          </>
        )}
      </TreeListNodeItem>
    );
  },
);

TreeListItem.displayName = 'TreeListItem';
